import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getSiteSyncStatus,
  startSiteSync,
  updateSiteSyncAuth,
  resyncSite,
  cancelSiteSync,
  getSiteSyncPages,
  getSiteSyncJourneys,
  getSiteSyncList,
  SiteSyncStartPayload,
} from "./site-sync.api";

export const SITE_SYNC_QUERY_KEY = "siteSyncStatus";

export const useSiteSyncStatus = (
  versionId: number | undefined,
  url?: string | undefined,
  isActive = false,
) =>
  useQuery({
    queryKey: [SITE_SYNC_QUERY_KEY, versionId, url || ""],
    queryFn: async () => {
      if (!versionId) return null;
      const res = await getSiteSyncStatus(versionId, url?.trim() || undefined);
      return res.data;
    },
    enabled: !!versionId,
    retry: 1,
    staleTime: 2000,
    // Each status poll fans out to ~5-6 backend DB queries (requirePermission +
    // globalVersionScope + the handler). Combined with the pages poll below, an
    // aggressive 3s cadence over a high-latency DB link can pile onto a saturated
    // connection pool. 5s keeps progress feeling live without amplifying load.
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data as any;
      // Poll while the backend reports an in-progress crawl, AND while a sync is
      // "active" from the caller's perspective (i.e. the user just hit
      // Start/Re-sync and we're awaiting a terminal status). Relying on
      // status==="crawling" alone is racy: a fast crawl can flip straight to
      // auth_required/failed before a poll ever runs, leaving the UI frozen on a
      // stale cached row. isActive keeps polling until a terminal status lands.
      if (isActive) return 5000;
      return data?.status === "crawling" ? 5000 : false;
    },
  });

export const useStartSiteSync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SiteSyncStartPayload) => {
      const res = await startSiteSync(payload);
      return res.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          SITE_SYNC_QUERY_KEY,
          variables.workspaceVersionId,
          variables.appUrl,
        ],
      });
    },
  });
};

export const useUpdateSiteSyncAuth = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      syncId,
      auth,
    }: {
      syncId: string | number;
      auth: SiteSyncStartPayload["auth"];
    }) => updateSiteSyncAuth(syncId, auth),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SITE_SYNC_QUERY_KEY] });
    },
  });
};

export const useResyncSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      syncId,
      auth,
    }: {
      syncId: string | number;
      auth?: SiteSyncStartPayload["auth"];
    }) => {
      const res = await resyncSite(syncId, auth);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SITE_SYNC_QUERY_KEY] });
    },
  });
};

export const useCancelSiteSync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (syncId: string | number) => cancelSiteSync(syncId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SITE_SYNC_QUERY_KEY] });
    },
  });
};

export const useSiteSyncPages = (
  syncId: string | number | undefined,
  isCrawling = false,
) =>
  useQuery({
    queryKey: ["siteSyncPages", syncId],
    queryFn: async () => {
      if (!syncId) return null;
      const res = await getSiteSyncPages(syncId);
      return res.data;
    },
    enabled: !!syncId,
    staleTime: isCrawling ? 0 : 10000,
    refetchOnWindowFocus: false,
    refetchInterval: isCrawling ? 5000 : false,
  });

export const useSiteSyncJourneys = (syncId: string | number | undefined) =>
  useQuery({
    queryKey: ["siteSyncJourneys", syncId],
    queryFn: async () => {
      if (!syncId) return null;
      const res = await getSiteSyncJourneys(syncId);
      return res.data;
    },
    enabled: !!syncId,
    staleTime: 30000,
    retry: 1,
  });

export const SITE_SYNC_LIST_KEY = "siteSyncList";

export const useSiteSyncList = (versionId: number | undefined) =>
  useQuery({
    queryKey: [SITE_SYNC_LIST_KEY, versionId],
    queryFn: async () => {
      if (!versionId) return null;
      const res = await getSiteSyncList(versionId);
      return res.data;
    },
    enabled: !!versionId,
    staleTime: 5000,
  });
